import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { buildOrganizationJsonLd, buildWebSiteJsonLd, DEFAULT_OG_IMAGE } from "@/lib/seo";
import Home from "./pages/Home";
import PricingPage from "./pages/PricingPage";
import FAQPage from "./pages/FAQPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import PaymentPolicyPage from "./pages/PaymentPolicyPage";
import ContactPage from "./pages/ContactPage";
import AboutUsPage from "./pages/AboutUsPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import ClinicRegistrationPage from "./pages/ClinicRegistrationPage";
import ClinicRegistrationSuccessPage from "./pages/ClinicRegistrationSuccessPage";
import ClinicLoginPage from "./pages/ClinicLoginPage";
import ClinicPortalPage from "./pages/ClinicPortalPage";
import PatientLoginPage from "./pages/PatientLoginPage";
import PatientProfilePage from "./pages/PatientProfilePage";
import BookingDetailPage from "./pages/BookingDetailPage";
import MaintenancePage from "./pages/MaintenancePage";
import TravelHubPage from "./pages/TravelHubPage";
import TreatmentsPage from "./pages/TreatmentsPage";
import TreatmentDetailPage from "./pages/TreatmentDetailPage";
import FindBestClinicPage from "./pages/FindBestClinicPage";
import EmailSettingsPage from "./pages/EmailSettingsPage";
import TravelHubSubPage from "./pages/TravelHubSubPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import EmergencyHotlinesPage from "./pages/EmergencyHotlinesPage";
import UrgentMedicalCareHoiAnPage from "./pages/UrgentMedicalCareHoiAnPage";
import UrgentMedicalCarePillarPage from "./pages/UrgentMedicalCarePillarPage";
import UrgentMedicalCareCityPage from "./pages/UrgentMedicalCareCityPage";
import UrgentMedicalCareLocationPage from "./pages/UrgentMedicalCareLocationPage";
import CommonIllnessesVietnamPage from "./pages/CommonIllnessesVietnamPage";
import DoctorToHotelVietnamPage from "./pages/DoctorToHotelVietnamPage";
import EnglishSpeakingDoctorVietnamPage from "./pages/EnglishSpeakingDoctorVietnamPage";
import PatientSupportVietnamPage from "./pages/PatientSupportVietnamPage";
import DoctorRecommendationVietnamPage from "./pages/DoctorRecommendationVietnamPage";
import MedicalTranslatorVietnamPage from "./pages/MedicalTranslatorVietnamPage";
import HowToChooseDoctorVietnamPage from "./pages/HowToChooseDoctorVietnamPage";
import MedicalTourismSupportVietnamPage from "./pages/MedicalTourismSupportVietnamPage";
import CareAfterDischargeVietnamPage from "./pages/CareAfterDischargeVietnamPage";
import HealthCheckupPackagesPage from "./pages/HealthCheckupPackagesPage";
import CompanyDetailsPage from "./pages/CompanyDetailsPage";
import GuidesPage from "./pages/GuidesPage";
import DownloadAppPage from "./pages/DownloadAppPage";
import CategoryTreatmentsPage from "./pages/CategoryTreatmentsPage";
import MediaHubPage from "./pages/MediaHubPage";
import { ALL_CATEGORY_LANDINGS } from "./data/categoryLandingContent";
import FloatingChat from "./components/FloatingChat";
import HreflangTags from "./components/HreflangTags";
import { LocaleRouter, parsePathLocale } from "./components/LocaleRouter";
import { Toaster } from "./components/ui/sonner";
import { LanguageProvider } from "./context/LanguageContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { AuthProvider } from "./context/AuthContext";
import { ContactInfoProvider } from "./contexts/ContactInfoContext";
import { CatalogProvider } from "./context/CatalogContext";
import { getMaintenanceStatus } from "./lib/api";

/* ─── Global scroll-to-top on every route change ─────────────────────────── */
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Allow the page to render before scrolling to the anchor
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        window.scrollTo(0, 0);
      }, 80);
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

/* ─── App content with maintenance gate ──────────────────────────────────── */
const AppContent = () => {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    getMaintenanceStatus()
      .then((res) => {
        setMaintenanceMode(res.data?.data?.maintenanceMode || false);
        setMaintenanceMessage(res.data?.data?.maintenanceMessage || '');
      })
      .catch(() => {
        // If API fails, don't block the app
        setMaintenanceMode(false);
      })
      .finally(() => setMaintenanceChecked(true));
  }, []);

  // Show nothing while checking (prevents flash)
  if (!maintenanceChecked) return null;

  const isClinicLoggedIn = !!localStorage.getItem('vc_clinic_access_token');
  const path = window.location.pathname;
  const isClinicPath = path.startsWith('/clinic-login') || path.startsWith('/clinic-registration');

  if (maintenanceMode && !isClinicLoggedIn && !isClinicPath) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  return (
    <>
      <ScrollToTop />
      <HreflangTags />
      <LocaleRouter>
        <LocalizedRoutes />
      </LocaleRouter>
      <FloatingChat />
    </>
  );
};

/* Renders the existing route table against a "locale-stripped" pathname so the
 * same Route definitions serve any of the 9 supported locales. */
const LocalizedRoutes = () => {
  const location = useLocation();
  const { stripped } = parsePathLocale(location.pathname);
  const effectiveLocation = stripped === location.pathname
    ? location
    : { ...location, pathname: stripped };

  return (
    <Routes location={effectiveLocation}>
      {/* Main Pages */}
      <Route path="/" element={<Home />} />

      {/* Deprecated directory routes — redirect to treatment-led flow */}
      <Route path="/clinics" element={<Navigate to="/treatments" replace />} />
      <Route path="/clinic/:id" element={<Navigate to="/treatments" replace />} />
      <Route path="/booking/confirmation" element={<Navigate to="/treatments" replace />} />
      <Route path="/booking/:id" element={<Navigate to="/treatments" replace />} />
      <Route path="/doctors/:id" element={<Navigate to="/treatments" replace />} />

      {/* Patient Pages */}
      <Route path="/login" element={<PatientLoginPage />} />
      <Route path="/profile" element={<PatientProfilePage />} />
      <Route path="/bookings/:id" element={<BookingDetailPage />} />

      {/* Clinic Pages */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/clinic-registration" element={<ClinicRegistrationPage />} />
      <Route path="/clinic-registration/success" element={<ClinicRegistrationSuccessPage />} />
      <Route path="/clinic-login" element={<ClinicLoginPage />} />
      <Route path="/clinic-portal" element={<ClinicPortalPage />} />

      {/* Company Pages */}
      <Route path="/about" element={<AboutUsPage />} />
      <Route path="/company-details" element={<CompanyDetailsPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/download-app" element={<DownloadAppPage />} />
      <Route path="/media" element={<MediaHubPage />} />
      {/* Header CTA helpers — keep these stable for marketing links */}
      <Route path="/for-clinics" element={<Navigate to="/pricing" replace />} />
      <Route path="/request-treatment" element={<Navigate to="/#request-appointment" replace />} />
      <Route path="/treatments" element={<TreatmentsPage />} />
      <Route path="/treatments/:slug" element={<TreatmentDetailPage />} />
      <Route path="/find-best-treatment" element={<FindBestClinicPage />} />
      <Route path="/admin/email-settings" element={<EmailSettingsPage />} />
      <Route path="/travel-hub" element={<TravelHubPage />} />
      <Route path="/travel-hub/:slug" element={<TravelHubSubPage />} />

      {/* Legal & Support Pages */}
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/payment-policy" element={<PaymentPolicyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/emergency" element={<EmergencyHotlinesPage />} />
      <Route path="/urgent-medical-care" element={<UrgentMedicalCarePillarPage />} />
      <Route path="/doctor-to-hotel-vietnam" element={<DoctorToHotelVietnamPage />} />
      <Route path="/english-speaking-doctor-vietnam" element={<EnglishSpeakingDoctorVietnamPage />} />
      <Route path="/patient-support-vietnam" element={<PatientSupportVietnamPage />} />
      <Route path="/doctor-recommendation-vietnam" element={<DoctorRecommendationVietnamPage />} />
      <Route path="/medical-translator-vietnam" element={<MedicalTranslatorVietnamPage />} />
      <Route path="/how-to-choose-doctor-vietnam" element={<HowToChooseDoctorVietnamPage />} />
      <Route path="/medical-tourism-support-vietnam" element={<MedicalTourismSupportVietnamPage />} />
      <Route path="/care-after-discharge-vietnam" element={<CareAfterDischargeVietnamPage />} />
      <Route path="/health-checkup-packages-vietnam" element={<HealthCheckupPackagesPage />} />
      <Route path="/urgent-medical-care-hoi-an" element={<UrgentMedicalCareHoiAnPage />} />
      {/* Forward-compat city stubs — render the "Coming Soon" page until each
          city launches its own dedicated landing. The stub auto-redirects to
          the dedicated page once `status: 'live'` is flipped in urgentCareCities.js. */}
      {/* Da Nang is now covered by the combined Hoi An page — 301 to preserve SEO equity */}
      <Route path="/urgent-medical-care-da-nang"     element={<Navigate to="/urgent-medical-care-hoi-an" replace />} />
      <Route path="/urgent-medical-care-ho-chi-minh" element={<UrgentMedicalCareCityPage citySlug="ho-chi-minh" />} />
      <Route path="/urgent-medical-care-hanoi"       element={<UrgentMedicalCareCityPage citySlug="hanoi" />} />
      <Route path="/urgent-medical-care-nha-trang"   element={<UrgentMedicalCareCityPage citySlug="nha-trang" />} />
      <Route path="/urgent-medical-care-phu-quoc"    element={<UrgentMedicalCareCityPage citySlug="phu-quoc" />} />
      <Route path="/urgent-medical-care-sapa"        element={<UrgentMedicalCareCityPage citySlug="sapa" />} />
      {/* Confirm-on-contact location pages — 24/7 coordination, no 60-min / availability guarantee */}
      <Route path="/urgent-medical-care-hue"         element={<UrgentMedicalCareLocationPage key="hue" slug="hue" />} />
      <Route path="/urgent-medical-care-lang-co"     element={<UrgentMedicalCareLocationPage key="lang-co" slug="lang-co" />} />
      <Route path="/urgent-medical-care-dong-hoi"    element={<UrgentMedicalCareLocationPage key="dong-hoi" slug="dong-hoi" />} />
      <Route path="/urgent-medical-care-phong-nha"   element={<UrgentMedicalCareLocationPage key="phong-nha" slug="phong-nha" />} />
      <Route path="/common-illnesses-vietnam"        element={<CommonIllnessesVietnamPage />} />

      {/* ── SEO Category Landing Pages — long-form treatment-led landings ──
          Auto-generated from `data/categoryLandingContent.js`. To add a new
          category landing, append an entry to that file — no edits needed
          here. All entries render via the shared CategoryTreatmentsPage. */}
      {ALL_CATEGORY_LANDINGS.map((c) => (
        <Route
          key={c.categorySlug}
          path={c.urlSlug}
          element={<CategoryTreatmentsPage />}
        />
      ))}

      {/* ── Legacy SEO directory landings — 301 to the new category landings ── */}
      <Route path="/dental-clinics-vietnam"     element={<Navigate to="/dental-treatments-in-vietnam" replace />} />
      <Route path="/hair-transplant-vietnam"    element={<Navigate to="/hair-restoration-in-vietnam" replace />} />
      <Route path="/ivf-vietnam"                element={<Navigate to="/fertility-treatments-in-vietnam" replace />} />
      <Route path="/cosmetic-surgery-vietnam"   element={<Navigate to="/cosmetic-surgery-in-vietnam" replace />} />
      <Route path="/orthopedics-vietnam"        element={<Navigate to="/orthopedic-treatments-in-vietnam" replace />} />
      <Route path="/eye-care-vietnam"           element={<Navigate to="/eye-care-treatments-in-vietnam" replace />} />
      <Route path="/health-checkup-vietnam"     element={<Navigate to="/preventive-health-screening-in-vietnam" replace />} />

      <Route path="/news/:slug" element={<ArticleDetailPage />} />
    </Routes>
  );
};

function App() {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const orgJsonLd = buildOrganizationJsonLd(origin);
  const webSiteJsonLd = buildWebSiteJsonLd(origin);
  return (
    <HelmetProvider>
      <LanguageProvider>
        <CurrencyProvider>
          <AuthProvider>
            <ContactInfoProvider>
              <CatalogProvider>
              <Helmet>
                <html lang="en" />
                {/* Route pages own titles and descriptions. React 19 hoists
                    each Helmet instance without deduplicating these tags. */}
                <meta property="og:type" content="website" />
                <meta property="og:image" content={DEFAULT_OG_IMAGE} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
                <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
                <script type="application/ld+json">{JSON.stringify(webSiteJsonLd)}</script>
              </Helmet>
              <div className="App">
                <BrowserRouter>
                  <AppContent />
                </BrowserRouter>
                <Toaster position="top-center" />
              </div>
              </CatalogProvider>
            </ContactInfoProvider>
          </AuthProvider>
        </CurrencyProvider>
      </LanguageProvider>
    </HelmetProvider>
  );
}

export default App;
